import { Alert, Box, Button, CircularProgress, Paper, Snackbar, TextField, Typography } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import { useCallback, useEffect, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import EnglishFormService from '../../services/EnglishFormService';
import { taskErrorMessage } from '../../services/TaskTrackingService';
import { FileTable } from '../work/WorkFolderPage';

const SIZE = 25;

/** Task tools' English tab: the SRTs AV sends through the form, shown as a shared folder (rev6 Part 11, 6j-3). PMs and admins. */
const EnglishSrtsList = () => {
    useTrackUsage('EnglishSrtsList', 'tab', 'tasks');
    const [q, setQ] = useState('');
    const [page, setPage] = useState(0);
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [undo, setUndo] = useState(null);

    const load = useCallback(() => {
        EnglishFormService.srts(q.trim() || undefined, page, SIZE)
            .then((d) => {
                setData(d);
                setError('');
            })
            .catch((e) => setError(taskErrorMessage(e)));
    }, [q, page]);

    useEffect(() => {
        const wait = setTimeout(load, 250);
        return () => clearTimeout(wait);
    }, [load]);

    const download = async (file) => {
        try {
            window.location.href = (await EnglishFormService.download(file.id)).url;
        } catch (e) {
            setError(taskErrorMessage(e));
        }
    };

    const remove = async (file) => {
        try {
            await EnglishFormService.remove(file.id);
            setUndo(file);
            load();
        } catch (e) {
            setError(taskErrorMessage(e));
        }
    };

    const restore = async () => {
        const file = undo;
        setUndo(null);
        try {
            await EnglishFormService.restore(file.id);
            load();
        } catch (e) {
            setError(taskErrorMessage(e));
        }
    };

    const count = data ? `${data.total} ${data.total === 1 ? 'SRT' : 'SRTs'}` : '';
    return (
        <>
            <Paper component="section" variant="outlined" aria-label="English SRTs" sx={{ borderRadius: 2 }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', px: 2, pt: 2, pb: 1, flexWrap: 'wrap' }}>
                    <FolderIcon sx={{ color: '#90a4ae' }} aria-hidden />
                    <Box sx={{ flex: 1, minWidth: 160 }}>
                        <Typography component="div" sx={{ fontSize: 16, fontWeight: 500 }}>
                            English SRTs
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {q.trim() && data ? `${count} match` : count}
                        </Typography>
                    </Box>
                    <TextField
                        size="small"
                        label="Search by title"
                        value={q}
                        onChange={(e) => {
                            setQ(e.target.value);
                            setPage(0);
                        }}
                        sx={{ width: { xs: '100%', sm: 260 } }}
                    />
                </Box>
                {error && (
                    <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
                        {error}
                    </Alert>
                )}
                {!data && !error && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}
                {data?.files.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
                        {q.trim() ? 'No SRT has that title.' : 'No SRTs yet'}
                    </Typography>
                )}
                {data?.files.length > 0 && (
                    <FileTable files={data.files} uploads={[]} canDelete deleting={{}} actions={{ download, remove }} />
                )}
                {data && data.total > SIZE && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, py: 1 }}>
                        <Button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                            Previous
                        </Button>
                        <Button disabled={(page + 1) * SIZE >= data.total} onClick={() => setPage((p) => p + 1)}>
                            Next
                        </Button>
                    </Box>
                )}
            </Paper>
            <Snackbar
                open={!!undo}
                autoHideDuration={8000}
                onClose={() => setUndo(null)}
                message={undo ? `${undo.name} deleted. It stays for 30 days.` : ''}
                action={
                    <Button color="inherit" size="small" onClick={restore}>
                        Undo
                    </Button>
                }
            />
        </>
    );
};

export default EnglishSrtsList;
